// iter87.194 — Trophy Tokens admin tab.
//
// Phase 1 shape: Config + Roster + Preview + Finalize in one Takeover.
// - Admin toggles the feature on/off.
// - Admin sets allocation mode (per_player | total_pot), amount, withhold_pct.
// - Roster shows every active crewmate with an opt-in badge (all "opted in"
//   in .194 — iter87.195 will layer in real opt-out signals).
// - Preview auto-computes pot_base / withheld / winner_pot from config + roster.
// - Admin picks the winner(s) via checkbox (multiple = tied 1st place, pot
//   splits evenly). Finalize writes the ledger idempotently and locks config.
// - Successful finalize re-loads state and shows the awarded rows.
//
// Backend behavior:
// - Config PUT gates on X-Participant-Id === league.admin_participant_id.
// - Finalize gates on same header; validates every winner is on the roster
//   AND opted in; re-running with the same winners returns idempotent replay.

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Save, CheckCircle2, Coins, AlertTriangle, Medal, Info, Lock, Undo2, Play } from "lucide-react";
import TrophyTokensVideoModal from "../TrophyTokensVideoModal";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
// iter87.390 (user-reported) — native window.prompt/window.confirm are
// unreliable on mobile Chrome/Safari (iOS Safari silently drops them,
// some mobile Chrome builds suppress them entirely). Swap for the same
// in-app dialog pattern already used by Danger Zone's Mark Complete /
// Clear Draft confirms.
import { useBrutalConfirm } from "@/components/BrutalConfirm";
import { Switch } from "@/components/ui/switch";
// iter87.554 — Squares templates render their own dedicated config/
// roster/lock/finalize UI instead of the generic Trophy Tokens form
// below (early return in the component body) — this tab is now the
// single admin surface for Squares, replacing the standalone Squares
// tab + most of the Scoring tab's old generic fallback content for
// those 4 templates. Every other template's Trophy Tokens is untouched.
import SquaresTokensTab from "./SquaresTokensTab";

const DEFAULT_CONFIG = {
    enabled: false,
    allocation_mode: "per_player",
    amount: 10,
    withhold_pct: 0,
    game_type: "winner_take_all",
    podium_splits: [50, 30, 20],
    podium_fallback: "winner_take_all",
};

const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd" };

function rankLabel(rank, size) {
    // iter87.384 (testing-agent-reported) — ORDINAL only covers slots
    // 1-3 (there are only 3 paid podium slots), so a tie that's bigger
    // than the slots it consumes (e.g. 4 people tied for 1st, only
    // consuming slots 1-3) must never fall through to a raw number
    // like "1st-4". Cap the displayed end-rank at 3 and call out the
    // real tie size separately when it doesn't match the slot span.
    if (size <= 1) return ORDINAL[rank] || `${rank}th`;
    const endRank = Math.min(rank + size - 1, 3);
    const slotsSpanned = endRank - rank + 1;
    const tieNote = slotsSpanned === size ? "(tied)" : `(${size}-way tie)`;
    if (endRank === rank) return `${ORDINAL[rank] || rank} ${tieNote}`;
    return `${ORDINAL[rank] || rank}–${ORDINAL[endRank] || endRank} ${tieNote}`;
}

function StatPill({ label, value, tone = "cyber", testId }) {
    const tones = {
        cyber: "border-cyber/40 bg-cyber/10 text-cyber",
        volt: "border-volt/40 bg-volt/10 text-volt",
        blaze: "border-blaze/40 bg-blaze/10 text-blaze",
        zinc: "border-zinc-700 bg-zinc-900 text-zinc-300",
    };
    return (
        <div className={`rounded-lg border p-3 ${tones[tone] || tones.zinc}`} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
            <div className="text-xl font-display font-black mt-1 tabular-nums">{value}</div>
        </div>
    );
}

export default function TrophyTokensTab({ tournament, league, onChanged }) {
    const tid = tournament?.id;
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    // iter87.196 — Server-persisted "enabled" flag (independent of local
    // draft state). Pot Preview + Roster surface only when this is true,
    // NOT when the local checkbox is flipped but Save Config hasn't been
    // pressed yet. Fixes the bug where flipping OFF-mid-draft (via any
    // roster action that triggers reload) would silently hide the tile.
    const [savedEnabled, setSavedEnabled] = useState(false);
    // iter87.384 (testing-agent-reported) — Preview breakdown, roster
    // rank badges and the finalized banner must reflect the SAVED
    // game_type, not the local draft. Otherwise flipping the toggle
    // pre-save makes the podium breakdown flash on/off against stale
    // server data that doesn't match what's being previewed.
    const [savedGameType, setSavedGameType] = useState("winner_take_all");
    const [proPlusOk, setProPlusOk] = useState(true);
    const [roster, setRoster] = useState([]);
    const [preview, setPreview] = useState(null);
    const [derivedWinnerIds, setDerivedWinnerIds] = useState([]);
    const [podiumGroups, setPodiumGroups] = useState([]);
    const [alreadyFinalized, setAlreadyFinalized] = useState(false);
    const [addedToSeasonAt, setAddedToSeasonAt] = useState(null);
    // iter87.533 (testing-agent-reported) — the video modal is rendered
    // OUTSIDE the Popover and controlled from here. A Dialog trigger
    // living INSIDE PopoverContent would unmount along with the
    // Popover the instant we close it (confirmed regression) — so the
    // Finalize info popover only closes itself and flips this flag;
    // the actual <TrophyTokensVideoModal> below is a sibling, never a
    // descendant of the Popover that can no longer affect it.
    const [finalizeInfoOpen, setFinalizeInfoOpen] = useState(false);
    const [walkthroughOpen, setWalkthroughOpen] = useState(false);
    const [addingToSeason, setAddingToSeason] = useState(false);
    const [removingFromSeason, setRemovingFromSeason] = useState(false);
    const [tournamentEnded, setTournamentEnded] = useState(false);
    const [locked, setLocked] = useState(false);
    // iter87.387 (user-requested) — Early admin-triggered pool lock,
    // distinct from the generic `locked` flag (which also fires on
    // natural kickoff/wrap). Lets the banner/copy say "you locked
    // this early" instead of "the tournament started".
    const [poolLocked, setPoolLocked] = useState(false);
    const [poolLockedAt, setPoolLockedAt] = useState(null);
    const [lockingPool, setLockingPool] = useState(false);
    // iter87.390 (user-reported) — type-to-confirm dialog state for Lock
    // Pool, replacing window.prompt (broken on mobile Chrome/Safari).
    const [lockDialogOpen, setLockDialogOpen] = useState(false);
    const [lockConfirmText, setLockConfirmText] = useState("");
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [ledger, setLedger] = useState([]);
    const [saving, setSaving] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [unfinalizing, setUnfinalizing] = useState(false);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        if (!tid) return;
        setLoading(true);
        try {
            const [cfgResp, rosterResp, previewResp] = await Promise.all([
                api.trophyTokensGetConfig(tid),
                api.trophyTokensGetRoster(tid),
                api.trophyTokensGetPreview(tid),
            ]);
            // Merge over defaults so pre-iter87.383 stored configs (no
            // game_type / podium_splits / podium_fallback keys) don't crash
            // the podium sliders on `config.podium_splits[0]`.
            const serverCfg = { ...DEFAULT_CONFIG, ...(cfgResp.config || {}) };
            setConfig(serverCfg);
            setSavedEnabled(!!serverCfg.enabled);
            setSavedGameType(serverCfg.game_type || "winner_take_all");
            setProPlusOk(cfgResp.pro_plus_ok !== false);
            setRoster(rosterResp.roster || []);
            setPreview(previewResp.preview || null);
            setLocked(!!previewResp.locked);
            setPoolLocked(!!(previewResp.pool_locked ?? cfgResp.pool_locked));
            setPoolLockedAt(previewResp.pool_locked_at ?? cfgResp.pool_locked_at ?? null);
            setAlreadyFinalized(previewResp.already_finalized || false);
            setAddedToSeasonAt(previewResp.added_to_season_at || null);
            setTournamentEnded(!!previewResp.tournament_ended);
            setLedger(previewResp.ledger || []);
            setDerivedWinnerIds(previewResp.derived_winner_ids || []);
            setPodiumGroups(previewResp.podium_groups || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load trophy tokens");
        }
        setLoading(false);
    }, [tid]);

    useEffect(() => { reload(); }, [reload]);

    const saveConfig = async () => {
        setSaving(true);
        try {
            await api.trophyTokensPutConfig(tid, config);
            toast.success("Trophy tokens config saved");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    const setState = async (pid, targetState) => {
        // iter87.199 — Two-stage state machine on the admin surface.
        // targetState ∈ { "opted_in", "confirmed", "dropped" }
        //   - "opted_in"  → POST /optin { opted_in: true }
        //   - "confirmed" → POST /confirm
        //   - "dropped"   → POST /optin { opted_in: false }
        // Confirm is the only endpoint that works post-lock (failsafe).
        if (!savedEnabled) return;
        if (alreadyFinalized) return;
        try {
            if (targetState === "confirmed") {
                await api.trophyTokensConfirm(tid, pid);
                toast.success("Confirmed");
            } else if (targetState === "opted_in") {
                await api.trophyTokensSetOptIn(tid, pid, true);
                toast.success("Opted in — awaiting confirm");
            } else if (targetState === "dropped") {
                await api.trophyTokensSetOptIn(tid, pid, false);
                toast.success("Dropped");
            }
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "State change failed");
        }
    };

    const doLockPool = () => {
        // iter87.390 (user-reported) — Opens the in-app type-to-confirm
        // modal (below) instead of window.prompt, which mobile Chrome
        // and Safari silently no-op or suppress entirely.
        setLockConfirmText("");
        setLockDialogOpen(true);
    };

    const executeLockPool = async () => {
        if (lockConfirmText.trim() !== "LOCK") {
            toast.error("Type LOCK to confirm");
            return;
        }
        setLockingPool(true);
        try {
            const resp = await api.trophyTokensLockPool(tid);
            toast.success(
                resp.dropped_count > 0
                    ? `Pool locked · dropped ${resp.dropped_count} unconfirmed crewmate${resp.dropped_count === 1 ? "" : "s"}`
                    : "Pool locked · config is now frozen",
            );
            setLockDialogOpen(false);
            setLockConfirmText("");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Lock pool failed");
        }
        setLockingPool(false);
    };

    const doFinalize = async () => {
        // iter87.387 (user-requested) — Finalize permanently credits
        // tokens to each winner's rivalry-wide running total; make the
        // consequence explicit before it fires, matching the Reverse
        // Finalize confirm dialog already below.
        // iter87.390 (user-reported) — window.confirm swapped for
        // BrutalConfirm; native confirms are unreliable on mobile.
        const ok = await brutalConfirm({
            title: "Finalize Trophy Token awards now?",
            body: "This credits tokens to the derived winner(s) for this tournament. It does NOT add them to the rivalry's running Trophy Tokens total — use the separate Add to Rivalry Token Totals button for that. Finalize can only be undone by pressing Reverse Finalize afterward — don't finalize until the result is final.",
            confirmLabel: "Finalize awards",
            destructive: true,
        });
        if (!ok) return;
        setFinalizing(true);
        try {
            // iter87.196 — Winner is derived server-side from the tournament
            // leaderboard; we no longer send winner_participant_ids. The
            // body is kept purely for backward-compat with older clients.
            const resp = await api.trophyTokensFinalize(tid, []);
            const groups = resp.podium_groups || [];
            if (groups.length > 1) {
                const total = groups.reduce((s, g) => s + g.participant_ids.length, 0);
                toast.success(`Podium locked in — ${total} crewmate${total === 1 ? "" : "s"} paid out`);
            } else if (groups.length === 1) {
                const g = groups[0];
                toast.success(
                    g.participant_ids.length === 1
                        ? `Awarded ${g.tokens_each} tokens to the leaderboard winner`
                        : `${g.participant_ids.length}-way tie — ${g.tokens_each} tokens each`,
                );
            }
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Finalize failed");
        }
        setFinalizing(false);
    };

    const doUnfinalize = async () => {
        // iter87.197 — Simple confirm dialog so an accidental double-tap
        // doesn't nuke a real finalize.
        // iter87.390 (user-reported) — window.confirm swapped for
        // BrutalConfirm; native confirms are unreliable on mobile
        // (iOS Safari silently drops them).
        const ok = await brutalConfirm({
            title: "Reverse the Trophy Tokens finalize?",
            body: "This drops every awarded ledger row on this tournament"
                + (poolLocked
                    ? " and lets you re-run finalize with a different derived winner. The pool stays permanently locked (config can't be edited) since it was locked early."
                    : " and re-opens the config for edits.")
                + " Anyone who won tokens will lose them until a re-finalize.",
            confirmLabel: "Reverse finalize",
            destructive: true,
        });
        if (!ok) return;
        setUnfinalizing(true);
        try {
            const resp = await api.trophyTokensUnfinalize(tid);
            toast.success(
                resp.dropped_rows > 0
                    ? `Reversed · dropped ${resp.dropped_rows} ledger row${resp.dropped_rows === 1 ? "" : "s"}`
                    : "Reversed · no ledger rows found",
            );
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reverse finalize failed");
        }
        setUnfinalizing(false);
    };

    const doAddToSeason = async () => {
        // iter87.525 (user-requested) — Deliberately separate from
        // Finalize: an admin can finalize a tournament's results
        // without it instantly changing the rivalry-wide Trophy Token
        // race standings everyone sees in Your Tiny Crew.
        const ok = await brutalConfirm({
            title: "Add these tokens to the rivalry's season total?",
            body: "This folds this tournament's finalized tokens into the rivalry's season-long Trophy Token race, shown in Your Tiny Crew. Crewmates' season rank can change the moment you do this.",
            confirmLabel: "Add to rivalry totals",
        });
        if (!ok) return;
        setAddingToSeason(true);
        try {
            const resp = await api.trophyTokensAddToSeason(tid);
            toast.success(resp.already_added ? "Already added to the rivalry total" : "Added to the rivalry's season-long Trophy Token race");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't add to the rivalry total");
        }
        setAddingToSeason(false);
    };

    const doRemoveFromSeason = async () => {
        // iter87.528 (user-requested) — Undo for Add to Rivalry Totals
        // alone. Pulls this tournament's tokens back out of the season
        // race without touching the finalized ledger — the tournament
        // stays finalized; it just stops counting toward the season
        // total until Add to Rivalry Totals is pressed again.
        const ok = await brutalConfirm({
            title: "Remove these tokens from the rivalry's season total?",
            body: "This pulls this tournament's tokens back out of the rivalry's season-long Trophy Token race shown in Your Tiny Crew. The finalized awards for this tournament stay as-is — only the season total changes. Crewmates' season rank can change the moment you do this.",
            confirmLabel: "Remove from rivalry totals",
            destructive: true,
        });
        if (!ok) return;
        setRemovingFromSeason(true);
        try {
            await api.trophyTokensRemoveFromSeason(tid);
            toast.success("Removed from the rivalry's season-long Trophy Token race");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't remove from the rivalry total");
        }
        setRemovingFromSeason(false);
    };

    const optedInCount = useMemo(() => roster.filter(r => r.opted_in).length, [roster]);
    const derivedWinnerSet = useMemo(() => new Set(derivedWinnerIds), [derivedWinnerIds]);
    const derivedWinnerNames = useMemo(
        () => derivedWinnerIds
            .map(pid => (roster.find(r => r.id === pid) || {}).username || pid.slice(0, 6))
            .filter(Boolean),
        [derivedWinnerIds, roster],
    );
    // iter87.383 — Podium mode pays out ranks beyond just 1st, so the
    // roster badge needs to recognise every podium winner, not just
    // `derivedWinnerIds` (which stays rank-1-only for back-compat).
    const podiumRankByPid = useMemo(() => {
        const m = new Map();
        for (const g of podiumGroups) {
            for (const pid of g.participant_ids) m.set(pid, g.rank);
        }
        return m;
    }, [podiumGroups]);

    if (!league || !tournament) return null;

    // iter87.554 — Squares templates get their own dedicated UI
    // (already Pro+-gated internally) instead of the generic Trophy
    // Tokens form + its own separate Pro+ upsell card below.
    if ((tournament?.template || "").includes("_squares_")) {
        return <SquaresTokensTab tournament={tournament} league={league} onChanged={onChanged} />;
    }

    // iter87.194 — Defense-in-depth against a forged /trophy-tokens URL.
    // AdminPanel.jsx already hides the bento tile on non-Pro+ leagues;
    // this second check catches direct-URL access + guards against a
    // stale isProPlus flag from an in-flight downgrade.
    if (!proPlusOk) {
        return (
            <div className="space-y-4" data-testid="trophy-tokens-tab">
                <section className="brutal-card p-5 space-y-3 border-volt/40" data-testid="trophy-tokens-upgrade-cta">
                    <div className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-volt" />
                        <h3 className="font-display font-black uppercase text-sm">Trophy Tokens · Pro+ only</h3>
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        Trophy Tokens is a Pro+ subscriber perk. Upgrade this rivalry to unlock a per-tournament pot, per-player allocation, and one-tap winner finalize.
                    </p>
                    <p className="text-[11px] text-zinc-500 leading-relaxed italic">
                        Upgrade from your Rivalry Settings → Subscription tab, or head to your account page to manage Pro+ across all your rivalries.
                    </p>
                </section>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="trophy-tokens-tab">
            {/* iter87.535 (user-reported) — the walkthrough link was only
                reachable one click deep inside the Finalize-info popover,
                so admins landing on this tab never saw it. Surface the
                same video directly at the top of the tab, no extra click. */}
            <button
                type="button"
                data-testid="trophy-tokens-watch-walkthrough-top-btn"
                onClick={() => setWalkthroughOpen(true)}
                className="w-full rounded-xl border border-sky-400/40 bg-sky-400/[0.06] p-3 flex items-center gap-3 hover:bg-sky-400/[0.1] transition-colors text-left"
            >
                <span className="w-9 h-9 rounded-full bg-sky-400 flex items-center justify-center shrink-0">
                    <Play className="w-4 h-4 text-black ml-0.5" fill="currentColor" />
                </span>
                <span className="min-w-0">
                    <span className="block text-xs font-display font-black uppercase tracking-wide text-sky-400">
                        Watch how Trophy Tokens work
                    </span>
                    <span className="block text-[11px] text-zinc-500 leading-snug mt-0.5">
                        90-second walkthrough · config, roster, and finalize
                    </span>
                </span>
            </button>

            {/* ─── Section 1: Config ─────────────────────────────── */}
            <section className="brutal-card p-4 space-y-4" data-testid="trophy-tokens-config-card">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <h3 className="font-display font-black uppercase text-sm flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-cyber" /> Trophy Tokens · Config
                        </h3>
                        <p className="text-[11px] text-zinc-500 leading-relaxed mt-1">
                            Set how many tokens are up for grabs and how much of the pot gets held back as the house cut.
                            {alreadyFinalized ? " Config is frozen because this tournament's awards are already finalized." : ""}
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <span
                                data-testid="trophy-tokens-enabled-status"
                                className={`text-xs uppercase font-black tracking-widest ${config.enabled ? "text-volt" : "text-zinc-500"}`}
                            >
                                {config.enabled ? "On" : "Off"}
                            </span>
                            <Switch
                                checked={!!config.enabled}
                                onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                                disabled={alreadyFinalized || locked}
                                data-testid="trophy-tokens-enabled-toggle"
                            />
                        </label>
                        {!config.enabled && !alreadyFinalized && !locked && (
                            <span className="text-[9px] uppercase tracking-wider text-zinc-500" data-testid="trophy-tokens-enabled-hint">
                                Slide to turn on
                            </span>
                        )}
                    </div>
                </div>

                {locked && !poolLocked && !alreadyFinalized && (
                    <div className="border border-blaze/40 bg-blaze/10 rounded-lg p-2.5 flex items-start gap-2" data-testid="trophy-tokens-lock-banner">
                        <AlertTriangle className="w-3.5 h-3.5 text-blaze shrink-0 mt-0.5" />
                        <div className="text-[11px] text-blaze leading-relaxed">
                            Locked · tournament has started or already wrapped. Config and opt-ins are frozen — same rule as Power Pick and Champions Pick. Finalize below once results are in.
                        </div>
                    </div>
                )}

                {config.enabled && !savedEnabled && !locked && !alreadyFinalized && (
                    <div className="border border-cyber/40 bg-cyber/10 rounded-lg p-2.5 flex items-start gap-2" data-testid="trophy-tokens-unsaved-hint">
                        <AlertTriangle className="w-3.5 h-3.5 text-cyber shrink-0 mt-0.5" />
                        <div className="text-[11px] text-cyber leading-relaxed">
                            Unsaved · press <b>Save Config</b> to lock in these settings. The Pot Preview and roster IN / OUT toggles unlock once the config is saved.
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Allocation</label>
                        <select
                            value={config.allocation_mode}
                            onChange={(e) => setConfig({ ...config, allocation_mode: e.target.value })}
                            disabled={!config.enabled || alreadyFinalized || locked}
                            className="brutal-input text-sm py-2 w-full disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                            data-testid="trophy-tokens-mode-select"
                        >
                            <option value="per_player">Per player</option>
                            <option value="total_pot">Total pot</option>
                        </select>
                        <p className="text-[10px] text-zinc-500 italic mt-1">
                            {config.allocation_mode === "per_player"
                                ? "Amount × opt-in count = pot"
                                : "Amount is the fixed total pot"}
                        </p>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">
                            {config.allocation_mode === "per_player" ? "Tokens per player" : "Total pot amount"}
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="100000"
                            value={config.amount}
                            onChange={(e) => setConfig({ ...config, amount: parseInt(e.target.value || "0", 10) })}
                            disabled={!config.enabled || alreadyFinalized || locked}
                            className="brutal-input text-sm py-2 w-full font-mono disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                            data-testid="trophy-tokens-amount-input"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">
                            House cut · {config.withhold_pct}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="75"
                            step="5"
                            value={config.withhold_pct}
                            onChange={(e) => setConfig({ ...config, withhold_pct: parseInt(e.target.value, 10) })}
                            disabled={!config.enabled || alreadyFinalized || locked}
                            className="w-full disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed"
                            data-testid="trophy-tokens-withhold-slider"
                        />
                        <p className="text-[10px] text-zinc-500 italic mt-1">
                            0-75%. Withheld tokens stay off the leaderboard.
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={saveConfig}
                        disabled={saving || alreadyFinalized || locked}
                        className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutal disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                        data-testid="trophy-tokens-save-config-btn"
                    >
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save config"}
                    </button>
                </div>
            </section>

            {/* ─── Section 1.5: Game Type (iter87.383 — user-requested) ──
                Sits between Config (above) and Roster/Preview (below).
                Winner Take All = existing behavior (top score(s) split
                the whole pot). Podium splits 1st/2nd/3rd via 2 sliders —
                the 3rd slot is always the remainder so the 3 can never
                add up to anything but 100. */}
            <section className="brutal-card p-4 space-y-4" data-testid="trophy-tokens-gametype-card">
                <div>
                    <h3 className="font-display font-black uppercase text-sm flex items-center gap-2">
                        <Medal className="w-4 h-4 text-cyber" /> Game Type
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="trophy-tokens-gametype-info-btn"
                                    aria-label="How do ties and short rosters get split?"
                                    className="w-5 h-5 rounded-full bg-cyber/15 border border-cyber/40 text-cyber flex items-center justify-center shrink-0 normal-case active:scale-90 transition-transform"
                                >
                                    <Info className="w-3 h-3" strokeWidth={2.5} />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                data-testid="trophy-tokens-gametype-info-tooltip"
                                side="top"
                                align="start"
                                className="z-[60] w-80 bg-obsidian border-cyber/30 p-3 space-y-2"
                            >
                                <p className="text-[11px] text-zinc-300 leading-snug font-normal normal-case tracking-normal">
                                    <b className="text-cyber">How ties split the podium:</b> a tie "eats" however many spots the tied group is blocking. Everyone in that tie splits the combined value of those spots evenly — whoever's next in line still gets whatever's left.
                                </p>
                                <ul className="text-[10px] text-zinc-400 leading-snug font-normal normal-case tracking-normal list-disc pl-4 space-y-1">
                                    <li><b className="text-zinc-300">Tie for 1st (2 people):</b> they split 1st + 2nd's shares; the next scorer takes 3rd alone.</li>
                                    <li><b className="text-zinc-300">Tie for 1st (3+ people):</b> they split the entire pot — nobody else gets paid.</li>
                                    <li><b className="text-zinc-300">Tie for 2nd:</b> they split 2nd + 3rd's shares between them.</li>
                                    <li><b className="text-zinc-300">Tie for 3rd:</b> they split only 3rd's share.</li>
                                </ul>
                                <p className="text-[10px] text-zinc-500 leading-snug font-normal normal-case tracking-normal border-t border-white/10 pt-2">
                                    <b className="text-zinc-300">Fewer than 3 people in it?</b> The fallback below decides: Winner Take All hands the whole pot to 1st, or Redistribute scales up the spots that do exist so they still total 100%.
                                </p>
                            </PopoverContent>
                        </Popover>
                    </h3>
                    <p className="text-[11px] text-zinc-500 leading-relaxed mt-1">
                        Winner Take All pays the whole pot to 1st place. Podium splits it across 1st, 2nd and 3rd.
                    </p>
                </div>

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setConfig({ ...config, game_type: "winner_take_all" })}
                        disabled={!config.enabled || alreadyFinalized || locked}
                        className={`flex-1 pill text-xs font-bold uppercase border transition py-2 ${
                            config.game_type !== "podium"
                                ? "border-volt bg-volt/20 text-volt"
                                : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                        } disabled:opacity-60`}
                        data-testid="trophy-tokens-gametype-wta-btn"
                    >
                        Winner Take All
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfig({ ...config, game_type: "podium" })}
                        disabled={!config.enabled || alreadyFinalized || locked}
                        className={`flex-1 pill text-xs font-bold uppercase border transition py-2 ${
                            config.game_type === "podium"
                                ? "border-volt bg-volt/20 text-volt"
                                : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                        } disabled:opacity-60`}
                        data-testid="trophy-tokens-gametype-podium-btn"
                    >
                        Podium · Top 3
                    </button>
                </div>

                {config.game_type === "podium" && (
                    <div className="space-y-3 pt-2 border-t border-white/10" data-testid="trophy-tokens-podium-splits">
                        {/* iter87.388 (user-requested) — Bigger, bolder
                            track (taller bar + bigger thumbs) with the
                            live percentage printed INSIDE each colored
                            segment (not just in the legend below) —
                            hidden on a segment once it's too narrow to
                            fit the text so it never overflows into its
                            neighbor. */}
                        <div className="relative h-16 flex items-center px-1" data-testid="trophy-tokens-podium-track">
                            <div className="absolute left-1 right-1 h-11 rounded-2xl overflow-hidden flex pointer-events-none border-2 border-black/40">
                                <div
                                    className="h-full bg-cyber transition-[width] duration-150 flex items-center justify-center overflow-hidden"
                                    style={{ width: `${config.podium_splits[0]}%` }}
                                >
                                    {config.podium_splits[0] >= 12 && (
                                        <span className="text-sm font-display font-black text-black whitespace-nowrap" data-testid="trophy-tokens-podium-first-inline-pct">
                                            1st · {config.podium_splits[0]}%
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="h-full bg-zinc-300 transition-[width] duration-150 flex items-center justify-center overflow-hidden"
                                    style={{ width: `${config.podium_splits[1]}%` }}
                                >
                                    {config.podium_splits[1] >= 12 && (
                                        <span className="text-sm font-display font-black text-black whitespace-nowrap" data-testid="trophy-tokens-podium-second-inline-pct">
                                            2nd · {config.podium_splits[1]}%
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="h-full bg-blaze transition-[width] duration-150 flex items-center justify-center overflow-hidden"
                                    style={{ width: `${config.podium_splits[2]}%` }}
                                >
                                    {config.podium_splits[2] >= 12 && (
                                        <span className="text-sm font-display font-black text-black whitespace-nowrap" data-testid="trophy-tokens-podium-third-inline-pct">
                                            3rd · {config.podium_splits[2]}%
                                        </span>
                                    )}
                                </div>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={config.podium_splits[0]}
                                onChange={(e) => {
                                    const pos1 = parseInt(e.target.value, 10);
                                    const pos2 = Math.max(config.podium_splits[0] + config.podium_splits[1], pos1);
                                    setConfig({ ...config, podium_splits: [pos1, pos2 - pos1, 100 - pos2] });
                                }}
                                disabled={!config.enabled || alreadyFinalized || locked}
                                className="dual-range-input dual-range-input-lg z-20"
                                style={{ "--thumb-color": "#FFCC00" }}
                                aria-label="1st place percentage"
                                data-testid="trophy-tokens-podium-first-slider"
                            />
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="5"
                                value={config.podium_splits[0] + config.podium_splits[1]}
                                onChange={(e) => {
                                    const pos1 = config.podium_splits[0];
                                    const pos2 = Math.max(parseInt(e.target.value, 10), pos1);
                                    setConfig({ ...config, podium_splits: [pos1, pos2 - pos1, 100 - pos2] });
                                }}
                                disabled={!config.enabled || alreadyFinalized || locked}
                                className="dual-range-input dual-range-input-lg z-30"
                                style={{ "--thumb-color": "#D4D4D8" }}
                                aria-label="1st + 2nd place percentage boundary"
                                data-testid="trophy-tokens-podium-second-slider"
                            />
                        </div>
                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wide">
                            <span className="flex items-center gap-1.5 text-cyber">
                                <span className="w-2 h-2 rounded-full bg-cyber shrink-0" /> 1st · {config.podium_splits[0]}%
                            </span>
                            <span className="flex items-center gap-1.5 text-zinc-300">
                                <span className="w-2 h-2 rounded-full bg-zinc-300 shrink-0" /> 2nd · {config.podium_splits[1]}%
                            </span>
                            <span className="flex items-center gap-1.5 text-blaze" data-testid="trophy-tokens-podium-third-pct">
                                <span className="w-2 h-2 rounded-full bg-blaze shrink-0" /> 3rd · {config.podium_splits[2]}%
                            </span>
                        </div>

                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">
                                Not enough for a 3-player podium?
                            </p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setConfig({ ...config, podium_fallback: "winner_take_all" })}
                                    disabled={!config.enabled || alreadyFinalized || locked}
                                    className={`flex-1 pill text-[10px] font-bold uppercase border transition py-1.5 ${
                                        config.podium_fallback === "winner_take_all"
                                            ? "border-cyber bg-cyber/20 text-cyber"
                                            : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                                    } disabled:opacity-60`}
                                    data-testid="trophy-tokens-podium-fallback-wta-btn"
                                >
                                    Winner Take All
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConfig({ ...config, podium_fallback: "redistribute" })}
                                    disabled={!config.enabled || alreadyFinalized || locked}
                                    className={`flex-1 pill text-[10px] font-bold uppercase border transition py-1.5 ${
                                        config.podium_fallback === "redistribute"
                                            ? "border-cyber bg-cyber/20 text-cyber"
                                            : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500"
                                    } disabled:opacity-60`}
                                    data-testid="trophy-tokens-podium-fallback-redistribute-btn"
                                >
                                    Redistribute
                                </button>
                            </div>
                            <p className="text-[10px] text-zinc-500 italic mt-1">
                                {config.podium_fallback === "redistribute"
                                    ? "Missing slot(s) get proportionally folded into the spots that do have a winner."
                                    : "Falls back to 1st place taking the whole pot — 2nd/3rd get nothing."}
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={saveConfig}
                        disabled={saving || alreadyFinalized || locked}
                        className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutal disabled:opacity-60 flex items-center gap-1.5"
                        data-testid="trophy-tokens-save-gametype-btn"
                    >
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save config"}
                    </button>
                </div>
            </section>

            {/* ─── Section 2: Preview + Roster + Finalize ────────── */}
            {/* iter87.196 — Only render when the SAVED config is enabled,
                not when the local checkbox has flipped but Save Config
                hasn't been pressed yet. Prevents the mid-draft flash where
                a roster action would silently hide the preview. */}
            {savedEnabled && (
                <section className="brutal-card p-4 space-y-4" data-testid="trophy-tokens-preview-card">
                    <div>
                        <h3 className="font-display font-black uppercase text-sm flex items-center gap-2">
                            <Coins className="w-4 h-4 text-volt" /> Pot Preview
                            <Popover open={finalizeInfoOpen} onOpenChange={setFinalizeInfoOpen}>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="trophy-tokens-finalize-info-btn"
                                        aria-label="What does Finalize Awards do?"
                                        className="w-5 h-5 rounded-full bg-volt/15 border border-volt/40 text-volt flex items-center justify-center shrink-0 normal-case active:scale-90 transition-transform"
                                    >
                                        <Info className="w-3 h-3" strokeWidth={2.5} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    data-testid="trophy-tokens-finalize-info-tooltip"
                                    side="top"
                                    align="start"
                                    className="z-[60] w-80 bg-obsidian border-volt/30 p-3 space-y-2"
                                >
                                    <p className="text-[11px] text-zinc-300 leading-snug font-normal normal-case tracking-normal">
                                        <b className="text-volt">Finalize Awards</b> stays grayed out until the tournament actually ends — you can't award anything early. Once the tournament ends, pressing it automatically figures out the winner(s) from the leaderboard (no manual picking) and permanently credits them tokens for this tournament. A separate <b className="text-volt">Add to Rivalry Token Totals</b> button then folds that into the rivalry-wide season race shown in Your Tiny Crew.
                                    </p>
                                    <p className="text-[10px] text-zinc-500 leading-snug font-normal normal-case tracking-normal border-t border-white/10 pt-2">
                                        Once pressed, this button is replaced by a <b className="text-zinc-300">Reverse Finalize</b> button — tap that to undo it: it deletes the awarded rows so you can re-run Finalize. Anyone who already saw the tokens won't automatically know they were clawed back, so only use it right after a bad finalize.
                                    </p>
                                    <div className="border-t border-white/10 pt-2 normal-case">
                                        <button
                                            type="button"
                                            data-testid="trophy-tokens-info-watch-walkthrough-link"
                                            onClick={() => { setFinalizeInfoOpen(false); setWalkthroughOpen(true); }}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-400 hover:text-sky-300 transition-colors"
                                        >
                                            <span className="w-5 h-5 rounded-full bg-sky-400 flex items-center justify-center shrink-0">
                                                <Play className="w-2.5 h-2.5 text-black ml-0.5" fill="currentColor" />
                                            </span>
                                            Watch the walkthrough
                                        </button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <TrophyTokensVideoModal
                                open={walkthroughOpen}
                                onOpenChange={setWalkthroughOpen}
                                testId="trophy-tokens-info-watch-walkthrough-link-modal"
                            />
                        </h3>
                        <p className="text-[11px] text-zinc-500 leading-relaxed mt-1">
                            Live math based on the current roster. Winner is auto-derived from the tournament leaderboard once the results are in.
                        </p>
                    </div>

                    {loading ? (
                        <p className="text-xs text-zinc-500 italic">Loading…</p>
                    ) : preview ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <StatPill label="Opt-ins" value={preview.n_opted_in} tone="cyber" testId="trophy-tokens-stat-optins" />
                            <StatPill label="Pot base" value={preview.pot_base} tone="volt" testId="trophy-tokens-stat-potbase" />
                            <StatPill label={`House cut (${preview.withhold_pct}%)`} value={preview.withheld} tone="blaze" testId="trophy-tokens-stat-withheld" />
                            <StatPill label="Up for grabs" value={preview.winner_pot} tone="cyber" testId="trophy-tokens-stat-winnerpot" />
                        </div>
                    ) : null}

                    {/* iter87.383 — Podium breakdown: one row per paid
                        rank group (ties pool together into one row, e.g.
                        "1st-2nd (tied)"). Replaces the single winner-pot
                        number with a per-slot view when game_type is
                        podium. */}
                    {savedGameType === "podium" && (
                        <div className="space-y-1.5" data-testid="trophy-tokens-podium-breakdown">
                            {podiumGroups.length === 0 ? (
                                <p className="text-xs text-zinc-500 italic">No opted-in crewmates yet.</p>
                            ) : podiumGroups.map((g) => (
                                <div
                                    key={g.rank}
                                    className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 flex items-center justify-between gap-2"
                                    data-testid={`trophy-tokens-podium-group-${g.rank}`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest text-volt font-bold">
                                            {rankLabel(g.rank, g.participant_ids.length)}
                                        </div>
                                        <div className="text-xs font-bold truncate">{g.usernames.join(", ") || "—"}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-display font-black text-sm tabular-nums">{g.pool_tokens} tokens</div>
                                        <div className="text-[10px] text-zinc-500">{g.tokens_each} each · {Number(g.pct).toFixed(1).replace(/\.0$/, "")}%</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div>
                        <h4 className="font-display font-extrabold uppercase text-xs mb-2">
                            {alreadyFinalized ? "Winners (finalized)" : poolLocked ? "Roster (pool locked · no further moves)" : locked ? "Roster (post-lock · confirm to rescue)" : "Roster · Opt In → Confirm"}
                        </h4>
                        {poolLocked && (
                            <div className="border border-cyber/40 bg-cyber/10 rounded-lg p-2.5 flex items-start gap-2 mb-2" data-testid="trophy-tokens-pool-locked-banner">
                                <Lock className="w-3.5 h-3.5 text-cyber shrink-0 mt-0.5" />
                                <div className="text-[11px] text-cyber leading-relaxed">
                                    Pool locked by an admin{poolLockedAt ? ` (${new Date(poolLockedAt).toLocaleString()})` : ""} — config, opt-ins, and confirms are all permanently frozen. Confirmed crewmates are still eligible.
                                    {!tournamentEnded && !alreadyFinalized ? " Finalize unlocks once the tournament ends." : ""}
                                </div>
                            </div>
                        )}
                        <p className="text-[11px] text-zinc-500 leading-relaxed mb-2">
                            {alreadyFinalized
                                ? "This tournament is already finalized. Ledger below is read-only."
                                : poolLocked
                                    ? "Pool locked — no more Opt In, Out, or Confirm moves of any kind, even for stray rows. Winner is derived from the leaderboard when you finalize."
                                    : locked
                                        ? "Tournament has started — Opt In is frozen. You can still Confirm a stray Opt-In row as a failsafe. Winner is derived from the leaderboard when you finalize."
                                        : "Two-step: tap OPT IN first, then CONFIRM to lock the crewmate into the pot. Only Confirmed crewmates contribute + win. Roster fully locks at first kickoff (any lingering opt-ins auto-drop)."}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="trophy-tokens-roster">
                            {roster.map((r) => {
                                const state = r.state || (r.opted_in ? "confirmed" : "none");
                                const isDerivedWinner = savedGameType === "podium"
                                    ? podiumRankByPid.has(r.id)
                                    : derivedWinnerSet.has(r.id);
                                const winnerRank = podiumRankByPid.get(r.id);
                                const ledgerRow = ledger.find(l => l.participant_id === r.id);
                                // Pre-lock: all three states settable.
                                // Post natural-kickoff-lock: only /confirm works (failsafe).
                                // iter87.388 — Post admin Lock Pool: NOTHING works, not
                                // even the Confirm failsafe — that's the whole point of
                                // the explicit hard-stop button.
                                const canOptIn = !locked && !alreadyFinalized;
                                const canConfirm = !alreadyFinalized && !poolLocked;
                                const canDrop = !locked && !alreadyFinalized;
                                const stateLabel = ({
                                    "none": "Not opted in",
                                    "opted_in": "Opted in · awaiting confirm",
                                    "confirmed": "Confirmed",
                                    "dropped": "Dropped",
                                }[state] || state);
                                return (
                                    <div
                                        key={r.id}
                                        className={`rounded-lg border p-2.5 transition ${
                                            isDerivedWinner && state === "confirmed"
                                                ? "border-cyber bg-cyber/15 text-cyber"
                                                : state === "confirmed"
                                                    ? "border-volt/50 bg-volt/[0.06]"
                                                    : state === "opted_in"
                                                        ? "border-heat/50 bg-heat/[0.06]"
                                                        : state === "dropped"
                                                            ? "border-zinc-800 bg-zinc-950 text-zinc-600 opacity-60"
                                                            : "border-zinc-700 bg-zinc-900 text-zinc-400"
                                        }`}
                                        data-testid={`trophy-tokens-roster-row-${r.id}`}
                                        data-state={state}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <div className="text-left flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-bold text-xs truncate">{r.username}</span>
                                                    {isDerivedWinner && state === "confirmed" && (
                                                        <CheckCircle2
                                                            className="w-3.5 h-3.5 shrink-0"
                                                            data-testid={`trophy-tokens-derived-winner-${r.id}`}
                                                        />
                                                    )}
                                                    {savedGameType === "podium" && winnerRank && state === "confirmed" && (
                                                        <span className="text-[9px] font-black uppercase tracking-wide shrink-0" data-testid={`trophy-tokens-podium-rank-${r.id}`}>
                                                            {ORDINAL[winnerRank] || `${winnerRank}th`}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] opacity-80 mt-0.5">
                                                    {stateLabel}
                                                    {ledgerRow ? ` · +${ledgerRow.tokens_awarded} tokens` : ""}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                disabled={!canOptIn || state === "opted_in"}
                                                onClick={() => setState(r.id, "opted_in")}
                                                className={`flex-1 pill text-[9px] font-bold uppercase border transition ${
                                                    state === "opted_in"
                                                        ? "border-heat/70 bg-heat/25 text-heat"
                                                        : "border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-heat/50 hover:text-heat"
                                                } ${!canOptIn || state === "opted_in" ? "opacity-70 cursor-not-allowed" : ""}`}
                                                data-testid={`trophy-tokens-optin-btn-${r.id}`}
                                                title={locked ? "Roster is locked" : "Mark opted-in (awaiting confirm)"}
                                            >
                                                Opt In
                                            </button>
                                            <button
                                                type="button"
                                                disabled={!canConfirm || state === "confirmed"}
                                                onClick={() => setState(r.id, "confirmed")}
                                                className={`flex-1 pill text-[9px] font-bold uppercase border transition ${
                                                    state === "confirmed"
                                                        ? "border-volt/70 bg-volt/25 text-volt"
                                                        : "border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-volt/50 hover:text-volt"
                                                } ${!canConfirm || state === "confirmed" ? "opacity-70 cursor-not-allowed" : ""}`}
                                                data-testid={`trophy-tokens-confirm-btn-${r.id}`}
                                                title={poolLocked ? "Pool is locked — Confirm is blocked too" : locked ? "Confirm still works post-lock as a failsafe" : "Confirm — lock crewmate into the pot"}
                                            >
                                                Confirm
                                            </button>
                                            <button
                                                type="button"
                                                disabled={!canDrop || state === "dropped" || state === "none"}
                                                onClick={() => setState(r.id, "dropped")}
                                                className={`pill text-[9px] font-bold uppercase border transition px-2 ${
                                                    state === "dropped"
                                                        ? "border-zinc-500 bg-zinc-800 text-zinc-400"
                                                        : "border-zinc-700 bg-zinc-900 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
                                                } ${!canDrop || state === "dropped" || state === "none" ? "opacity-70 cursor-not-allowed" : ""}`}
                                                data-testid={`trophy-tokens-drop-btn-${r.id}`}
                                                title={locked ? "Roster is locked" : "Drop crewmate from the pot"}
                                            >
                                                Out
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            {roster.length === 0 && (
                                <p className="text-xs text-zinc-500 italic col-span-full">No crewmates on the roster yet.</p>
                            )}
                        </div>
                    </div>

                    {/* iter87.527 (user-requested) — All 3 actions (Lock
                        Pool, Finalize Awards, Add to Rivalry Totals) now
                        render together as a 3-card row at all times instead
                        of appearing/disappearing as state changes — the
                        operator was only ever seeing 2 of the 3 at once and
                        thought buttons were vanishing. Lock Pool moved off
                        the red/heat palette onto a neutral cyber one so
                        red is reserved exclusively for the destructive
                        Reverse Finalize action (they were previously the
                        same color and easy to confuse). */}
                    {!alreadyFinalized && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-white/10" data-testid="trophy-tokens-actions-row">
                            <div className="rounded-lg border border-cyber/30 bg-cyber/[0.04] p-3 flex flex-col justify-between gap-2">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-cyber font-bold flex items-center gap-1.5">
                                        <Lock className="w-3 h-3" /> 1 · Lock Pool <span className="text-zinc-500 normal-case font-normal">(optional)</span>
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-relaxed mt-1">
                                        {poolLocked
                                            ? "Locked — config, opt-ins, and confirms are permanently frozen."
                                            : "Freeze the roster + config early — no opt-ins, confirms, or edits after. Skip straight to Finalize if you don't need this."}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={doLockPool}
                                    disabled={!savedEnabled || poolLocked || lockingPool}
                                    className="brutal-btn text-xs py-2 bg-black border-cyber/70 text-cyber hover:bg-cyber/10 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                                    data-testid="trophy-tokens-lock-pool-btn"
                                    title={poolLocked ? "Pool already locked" : "Freezes config and blocks ALL opt-in/confirm moves, anytime before Finalize"}
                                >
                                    {poolLocked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                                    {lockingPool ? "Locking…" : poolLocked ? "Pool locked" : "Lock pool"}
                                </button>
                            </div>

                            <div className="rounded-lg border border-volt/30 bg-volt/[0.04] p-3 flex flex-col justify-between gap-2">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-volt font-bold flex items-center gap-1.5">
                                        <Trophy className="w-3 h-3" /> 2 · Finalize Awards
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-relaxed mt-1" data-testid="trophy-tokens-derived-winner-summary">
                                        {!tournamentEnded
                                            ? "Unlocks once the tournament ends — the last game hasn't been played yet."
                                            : derivedWinnerNames.length === 0
                                                ? "No opted-in crewmates — no tokens will be awarded."
                                                : savedGameType === "podium"
                                                    ? podiumGroups
                                                        .map(g => `${rankLabel(g.rank, g.participant_ids.length)}: ${g.usernames.join(", ")} → ${g.tokens_each} each`)
                                                        .join(" · ")
                                                    : derivedWinnerNames.length === 1
                                                        ? `Derived winner: ${derivedWinnerNames[0]} → ${preview?.winner_pot ?? 0} tokens`
                                                        : `${derivedWinnerNames.length}-way tie: ${derivedWinnerNames.join(", ")} → ${Math.floor((preview?.winner_pot ?? 0) / derivedWinnerNames.length)} tokens each`}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={doFinalize}
                                    disabled={finalizing || !tournamentEnded || derivedWinnerNames.length === 0 || (preview?.winner_pot ?? 0) === 0}
                                    className="brutal-btn text-xs py-2 bg-volt text-black border-black shadow-brutal disabled:opacity-60 flex items-center justify-center gap-1.5"
                                    data-testid="trophy-tokens-finalize-btn"
                                    title={!tournamentEnded ? "Unlocks once the tournament ends" : undefined}
                                >
                                    <Trophy className="w-3.5 h-3.5" />
                                    {finalizing ? "Finalizing…" : "Finalize awards"}
                                </button>
                            </div>

                            <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 flex flex-col justify-between gap-2" data-testid="trophy-tokens-add-to-season-card">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1.5">
                                        <Trophy className="w-3 h-3" /> 3 · Add to Rivalry Totals <span className="text-zinc-500 normal-case font-normal">(optional)</span>
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-relaxed mt-1">
                                        Folds this tournament's tokens into the rivalry's season-long race (Your Tiny Crew). Unlocks once Finalize Awards is pressed.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    disabled
                                    className="brutal-btn text-xs py-2 bg-black border-zinc-700 text-zinc-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                                    data-testid="trophy-tokens-add-to-season-btn"
                                    title="Unlocks once Finalize Awards is pressed"
                                >
                                    <Trophy className="w-3.5 h-3.5" />
                                    Add to rivalry token totals
                                </button>
                            </div>
                        </div>
                    )}

                    {alreadyFinalized && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-white/10" data-testid="trophy-tokens-actions-row">
                            <div className="rounded-lg border border-cyber/30 bg-cyber/[0.04] p-3 flex flex-col justify-between gap-2 opacity-80">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-cyber font-bold flex items-center gap-1.5">
                                        <Lock className="w-3 h-3" /> 1 · Lock Pool
                                    </div>
                                    <p className="text-[10px] text-zinc-500 leading-relaxed mt-1">
                                        {poolLocked ? "This tournament's pool was locked early." : "This tournament finalized without an early lock."}
                                    </p>
                                </div>
                                <div className="brutal-btn text-xs py-2 bg-black border-cyber/40 text-cyber/60 flex items-center justify-center gap-1.5" data-testid="trophy-tokens-lock-pool-btn">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> {poolLocked ? "Pool locked" : "Not used"}
                                </div>
                            </div>

                            <div className="rounded-lg border border-cyber/40 bg-cyber/10 p-3 flex flex-col justify-between gap-2" data-testid="trophy-tokens-finalized-banner">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-cyber font-bold flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3 h-3" /> 2 · Finalized
                                    </div>
                                    <p className="text-[11px] text-cyber leading-relaxed mt-1">
                                        {ledger.length} winner{ledger.length !== 1 ? "s" : ""} · {ledger.reduce((s, r) => s + (r.tokens_awarded || 0), 0)} tokens awarded. Reverse below to undo.
                                    </p>
                                    {ledger[0]?.game_type === "podium" && (
                                        <div className="space-y-1 mt-1.5" data-testid="trophy-tokens-finalized-podium-breakdown">
                                            {[...new Set(ledger.map(r => r.rank || 1))].sort((a, b) => a - b).map(rank => {
                                                const rows = ledger.filter(r => (r.rank || 1) === rank);
                                                const names = rows.map(r => (roster.find(x => x.id === r.participant_id) || {}).username || r.participant_id.slice(0, 6));
                                                return (
                                                    <div key={rank} className="text-[10px] text-zinc-400">
                                                        {rankLabel(rank, rows.length)}: {names.join(", ")} · {rows[0]?.tokens_awarded ?? 0} each
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={doUnfinalize}
                                    disabled={unfinalizing}
                                    className="brutal-btn text-xs py-2 bg-black border-heat/70 text-heat hover:bg-heat/10 disabled:opacity-60 flex items-center justify-center gap-1.5"
                                    data-testid="trophy-tokens-unfinalize-btn"
                                >
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    {unfinalizing ? "Reversing…" : "Reverse finalize"}
                                </button>
                            </div>

                            <div className={`rounded-lg border p-3 flex flex-col justify-between gap-2 ${addedToSeasonAt ? "border-volt/40 bg-volt/10" : "border-zinc-700 bg-zinc-900"}`} data-testid="trophy-tokens-add-to-season-card">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-volt font-bold flex items-center gap-1.5">
                                        <Trophy className="w-3 h-3" /> 3 · Add to Rivalry Totals
                                    </div>
                                    <p className="text-[11px] leading-relaxed mt-1 text-zinc-400">
                                        {addedToSeasonAt ? (
                                            <span className="text-volt flex items-center gap-1.5">
                                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> Added to the season-long race.
                                            </span>
                                        ) : (
                                            "Optional — folds this tournament's tokens into the rivalry's season-long Trophy Token race (Your Tiny Crew)."
                                        )}
                                    </p>
                                </div>
                                {addedToSeasonAt ? (
                                    <button
                                        type="button"
                                        onClick={doRemoveFromSeason}
                                        disabled={removingFromSeason}
                                        className="brutal-btn text-xs py-2 bg-black border-zinc-500 text-zinc-300 hover:border-zinc-300 disabled:opacity-60 flex items-center justify-center gap-1.5"
                                        data-testid="trophy-tokens-remove-from-season-btn"
                                        title="Pulls this tournament's tokens back out of the season total only — the finalized ledger is untouched"
                                    >
                                        <Undo2 className="w-3.5 h-3.5" />
                                        {removingFromSeason ? "Removing…" : "Remove from rivalry totals"}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={doAddToSeason}
                                        disabled={addingToSeason}
                                        className="brutal-btn text-xs py-2 bg-volt text-black border-black shadow-brutal disabled:opacity-60 flex items-center justify-center gap-1.5"
                                        data-testid="trophy-tokens-add-to-season-btn"
                                    >
                                        <Trophy className="w-3.5 h-3.5" />
                                        {addingToSeason ? "Adding…" : "Add to rivalry token totals"}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {optedInCount === 0 && !alreadyFinalized && (
                        <div className="border border-blaze/40 bg-blaze/10 rounded-lg p-3 flex items-start gap-2" data-testid="trophy-tokens-empty-roster-warning">
                            <AlertTriangle className="w-4 h-4 text-blaze shrink-0 mt-0.5" />
                            <div className="text-[11px] text-blaze leading-relaxed">
                                No opted-in crewmates yet. Toggle at least one crewmate IN above so the tournament winner has someone to claim the pot.
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* iter87.390 (user-reported) — Lock Pool type-to-confirm
                modal, matching the Clear Draft / Mark Complete dialog
                pattern (native window.prompt was silently broken on
                mobile Chrome/Safari). */}
            {lockDialogOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4"
                    data-testid="lock-pool-confirm-dialog"
                    onClick={(e) => { if (e.target === e.currentTarget) { setLockDialogOpen(false); setLockConfirmText(""); } }}
                >
                    <div className="brutal-card p-6 max-w-md w-full bg-ink border-black space-y-4">
                        <div className="flex items-start gap-3">
                            <Lock className="w-6 h-6 text-cyber shrink-0 mt-1" />
                            <div>
                                <h3 className="font-display font-black text-xl uppercase">Lock the Trophy Tokens pool?</h3>
                                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                                    Freezes Game Type + splits — no more config edits, ever. Drops every crewmate who's Opted In but not yet Confirmed. Blocks Confirm too — a hard stop, not just a natural kickoff lock. Works anytime: before kickoff, mid-tournament, or after. This is <strong className="text-zinc-200">PERMANENT</strong> — there is no unlock.
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                                Type <span className="text-cyber">LOCK</span> to confirm
                            </label>
                            <input
                                data-testid="lock-pool-confirm-input"
                                type="text"
                                value={lockConfirmText}
                                onChange={(e) => setLockConfirmText(e.target.value)}
                                className="brutal-input w-full"
                                placeholder="LOCK"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && lockConfirmText.trim() === "LOCK") {
                                        executeLockPool();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                data-testid="lock-pool-cancel-btn"
                                onClick={() => { setLockDialogOpen(false); setLockConfirmText(""); }}
                                className="brutal-btn-ghost text-xs"
                                disabled={lockingPool}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="lock-pool-confirm-btn"
                                onClick={executeLockPool}
                                disabled={lockingPool || lockConfirmText.trim() !== "LOCK"}
                                className="brutal-btn text-xs bg-cyber text-black border-black shadow-brutal disabled:opacity-40"
                            >
                                {lockingPool ? "Locking…" : "Lock pool"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {confirmHost}
        </div>
    );
}
